import React, { useState, useEffect, useMemo } from 'react';
import { useQuery } from '@tanstack/react-query';
import {
  Box,
  Modal,
  IconButton,
  Paper,
  FormControlLabel,
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
  Stack,
  Select,
  MenuItem,
  SelectChangeEvent,
  Divider
} from '@mui/material';
import Close from '@mui/icons-material/Close';
import Validator from 'validator';
import Checkbox from '@mui/material/Checkbox';
import { TextView, Button, TextField, Alert } from '../../../../atoms';

import { callCreateBillingAccount, callUpdateBillingAccount } from '../../../../particles';
import { callGetMarkupValues } from '@api';
import { DEFAULT_FALLBACK_MARKUP_TYPE } from '@particles';
import { DatePicker } from '@mui/x-date-pickers';
import { DateTime } from 'luxon';

interface AddBillingAccountProps {
  open: boolean;
  selectedBillingAccount?: BillingAccount;
  onClose: () => void;
  onSuccess: () => void;
}

type PriceModelOption = { value: string; label: string; model?: string };

// priceModel is stored as the full MarkupMargins.type slug — no mapping needed.
const markupTypeToPriceModel = (type: string): string => type;

// Mirrors server/src/usecases/billingAccount/billingPeriod.ts semantics.
// billingDayOfMonth is the FIRST day of the cycle (inclusive); the cycle
// runs from that day to the day BEFORE the same billingDay next month
// (both inclusive). Day 29-31 clamp to the last day of shorter months
// so boundaries stay gap-free.
const BILLING_ZONE = 'Europe/London';
const clampDayToMonthEnd = (year: number, month: number, day: number): number => {
  const daysInMonth = DateTime.fromObject({ year, month, day: 1 }, { zone: BILLING_ZONE }).daysInMonth ?? 28;
  return Math.min(Math.max(1, day), daysInMonth);
};
const anchorOfMonth = (billingDay: number, year: number, month: number): DateTime =>
  DateTime.fromObject(
    { year, month, day: clampDayToMonthEnd(year, month, billingDay) },
    { zone: BILLING_ZONE }
  ).startOf('day');
const currentPeriodForToday = (billingDay: number): { start: DateTime; end: DateTime } => {
  const today = DateTime.now().setZone(BILLING_ZONE).startOf('day');
  const thisAnchor = anchorOfMonth(billingDay, today.year, today.month);
  if (today >= thisAnchor) {
    const next = today.plus({ months: 1 });
    return { start: thisAnchor, end: anchorOfMonth(billingDay, next.year, next.month).minus({ days: 1 }) };
  }
  const prev = today.minus({ months: 1 });
  return { start: anchorOfMonth(billingDay, prev.year, prev.month), end: thisAnchor.minus({ days: 1 }) };
};
// Mirror of server's nextAnchorAfter. Used by the edit path: on a live
// account, changing billingDayOfMonth keeps the current cycle's start and
// slides the end to (next anchor of new day) - 1 day.
const nextAnchorAfter = (billingDay: number): DateTime => {
  const today = DateTime.now().setZone(BILLING_ZONE).startOf('day');
  const thisAnchor = anchorOfMonth(billingDay, today.year, today.month);
  if (thisAnchor > today) return thisAnchor;
  const next = today.plus({ months: 1 });
  return anchorOfMonth(billingDay, next.year, next.month);
};
// Cycle-length bounds must match server (CYCLE_LENGTH_MIN/MAX_DAYS in
// billingAccount.interactor.ts). Outside this range the server requires
// force=true; we surface the same warning in the UI before submit.
const CYCLE_LENGTH_MIN_DAYS = 20;
const CYCLE_LENGTH_MAX_DAYS = 40;

const businessTypeOptions = [
  { value: 'estate', label: 'Estate' },
  { value: 'tourism', label: 'Tourism' },
  { value: 'mortgage', label: 'Mortgage' },
  { value: 'other', label: 'Other' }
] as const;

// billingDayOfMonth accepts 1-31. Days 29-31 are Stripe-style clamped to
// the last day of shorter months (Feb 28/29; Apr/Jun/Sep/Nov 30), so every
// calendar month is billed exactly once with no gaps or overlaps.
const MAX_BILLING_DAY = 31;
const defaultBillingDay = Math.min(DateTime.now().setZone(BILLING_ZONE).day, MAX_BILLING_DAY);
const defaultPeriod = currentPeriodForToday(defaultBillingDay);

const defaultBillingAccount: {
  companyName: string;
  twilioSecret?: string;
  twilioToken?: string;
  locationId?: string;
  reapitCompanyId?: string;
  totalToken?: string;
  subscriptionCost: string;
  status: 'live' | 'staging' | 'inactive';
  priceModel: string;
  businessType: 'tourism' | 'estate' | 'mortgage' | 'other';
  billingDayOfMonth: number;
  subscriptionDate: DateTime | null;
  nextSubscriptionDate: DateTime | null;
  launchDate: DateTime | null;
} = {
  companyName: '',
  twilioSecret: '',
  twilioToken: '',
  locationId: '',
  reapitCompanyId: '',
  totalToken: '25000',
  subscriptionCost: '0',
  priceModel: 'ai_processing',
  businessType: 'estate',
  status: 'staging',
  // Default to today's day of month; the cycle is then derived from the
  // clamped-anchor math so subscriptionDate / nextSubscriptionDate are
  // always in sync with billingDayOfMonth.
  billingDayOfMonth: defaultBillingDay,
  subscriptionDate: defaultPeriod.start,
  nextSubscriptionDate: defaultPeriod.end,
  launchDate: null
};

const defaultErrorBillingAccount: {
  companyName: string;
  twilioSecret?: string;
  twilioToken?: string;
  locationId: string;
  reapitCompanyId?: string;
  totalToken: string;
  subscriptionDate: string;
  launchDate: string;
  priceModel: string;
  status: string;
} = {
  companyName: '',
  twilioSecret: '',
  twilioToken: '',
  locationId: '',
  reapitCompanyId: '',
  totalToken: '',
  subscriptionDate: '',
  launchDate: '',
  priceModel: '',
  status: ''
};

const SectionLabel: React.FC<{ children: React.ReactNode }> = ({ children }) => (
  <TextView $fontSize={13} $fontWeight={700} $fontColor="#475569" $textTransform="uppercase">
    {children}
  </TextView>
);

const AddBillingAccount: React.FC<AddBillingAccountProps> = ({
  onClose,
  open,
  onSuccess,
  selectedBillingAccount
}: AddBillingAccountProps) => {
  const [loading, setLoading] = useState<boolean>(false);
  const [checkbox, setCheckBox] = useState<boolean>(false);
  const [checkDialog, setCheckDialog] = useState<boolean>(false);
  const [cycleWarnOpen, setCycleWarnOpen] = useState<boolean>(false);
  const [values, setValues] = React.useState(defaultBillingAccount);
  // Price-model options are fetched from MarkupMargins on dialog open — single source of truth.

  const [error, setError] = useState<{
    title: string;
    message: string;
  }>();

  const [errors, setErrors] = useState(defaultErrorBillingAccount);

  useEffect(() => {
    if (selectedBillingAccount) {
      setValues({
        companyName: selectedBillingAccount.companyName,
        twilioSecret: selectedBillingAccount.twilioSecret,
        twilioToken: selectedBillingAccount.twilioToken,
        locationId: selectedBillingAccount.locationId,
        reapitCompanyId: selectedBillingAccount.reapitCompanyId,
        status: selectedBillingAccount.status ?? 'staging',
        priceModel: selectedBillingAccount.priceModel,
        businessType: (selectedBillingAccount.businessType as 'tourism' | 'estate' | 'mortgage' | 'other') ?? 'estate',
        totalToken: selectedBillingAccount.totalToken?.toString(),
        subscriptionCost: selectedBillingAccount.subscriptionCost?.toString() ?? '0',
        billingDayOfMonth:
          selectedBillingAccount.billingDayOfMonth ??
          (selectedBillingAccount.nextSubscriptionDate
            ? Math.max(1, DateTime.fromISO(selectedBillingAccount.nextSubscriptionDate).minus({ days: 1 }).day)
            : DateTime.now().day),
        subscriptionDate: DateTime.fromISO(selectedBillingAccount.subscriptionDate),
        nextSubscriptionDate: DateTime.fromISO(selectedBillingAccount.nextSubscriptionDate),
        launchDate: selectedBillingAccount.launchDate
          ? DateTime.fromISO(selectedBillingAccount.launchDate)
          : null
      });
    }
  }, [selectedBillingAccount]);

  const markupQuery = useQuery({
    queryKey: ['markup-values/catalogue'],
    queryFn: () => callGetMarkupValues(),
    enabled: open,
    // Shared cache key with accountDetails — see that file for the
    // 30-minute rationale.
    staleTime: 30 * 60 * 1000
  });
  const priceModelOptionsLoading = markupQuery.isFetching;
  const priceModelOptions: PriceModelOption[] = useMemo(() => {
    if (markupQuery.data?.code !== 200 || !markupQuery.data.data?.data) return [];
    // The AI fallback tier shares the `ai_processing` prefix so its usage still rolls
    // up into AI totals, which means the prefix test alone would offer it here. It is
    // billing configuration rather than a sellable plan, and this modal is the only
    // place a priceModel is written, so exclude it by name.
    //
    // Compared against the constant rather than the `ai_fallback_markup_type` lookup:
    // this is the one surface that must never be wrong, and a constant cannot fail to
    // load. The cost is that repointing the fallback at a different tier means editing
    // this constant too, not just the lookup row.
    return markupQuery.data.data.data
      .filter((item) => item.type.startsWith('ai_processing') && item.type !== DEFAULT_FALLBACK_MARKUP_TYPE)
      .map((item) => ({
        value: markupTypeToPriceModel(item.type),
        label: item.title,
        // Surface the AI Response model so admins can see which model the
        // plan will use when picking a tier.
        model: item.responseModel
      }));
  }, [markupQuery.data]);

  const handleChange = (prop: string) => (event: React.ChangeEvent<HTMLInputElement>) => {
    if (prop === 'totalToken') {
      setValues({ ...values, [prop]: event.target.value.replace(/[^\d-]/g, '') });
      return;
    }

    if (prop === 'subscriptionCost') {
      setValues({ ...values, [prop]: event.target.value.replace(/[^\d.]/g, '') });
      return;
    }

    setValues({ ...values, [prop]: event.target.value });
  };

  const handleChangeStatus = (event: SelectChangeEvent) => {
    setValues({ ...values, status: event.target.value as 'live' | 'staging' | 'inactive' });
  };

  const handleChangePriceModel = (event: SelectChangeEvent) => {
    setValues({ ...values, priceModel: event.target.value });
  };

  const handleChangeBusinessType = (event: SelectChangeEvent) => {
    setValues({ ...values, businessType: event.target.value as 'tourism' | 'estate' | 'mortgage' | 'other' });
  };

  const handleClose = () => {
    onClose();
    setValues(defaultBillingAccount);
    setErrors(defaultErrorBillingAccount);
    setError(undefined);
  };

  // Derived flags for the billing-day change gate. Compute cycle length
  // from the current start/end so the warning banner and the pre-submit
  // check both agree with what the server will compute from the same
  // subscriptionDate + new billingDayOfMonth.
  const isEditMode = Boolean(selectedBillingAccount);
  const originalBillingDay = selectedBillingAccount?.billingDayOfMonth;
  const isDayChanged =
    isEditMode &&
    typeof originalBillingDay === 'number' &&
    originalBillingDay !== values.billingDayOfMonth;
  const projectedCycleLen =
    values.subscriptionDate && values.nextSubscriptionDate
      ? Math.round(values.nextSubscriptionDate.diff(values.subscriptionDate, 'days').days) + 1
      : null;
  const cycleOutOfBounds =
    projectedCycleLen !== null &&
    (projectedCycleLen < CYCLE_LENGTH_MIN_DAYS || projectedCycleLen > CYCLE_LENGTH_MAX_DAYS);

  const submitPayload = (force: boolean) => {
    const {
      companyName,
      twilioSecret,
      twilioToken,
      totalToken,
      subscriptionCost,
      locationId,
      subscriptionDate,
      nextSubscriptionDate,
      launchDate,
      reapitCompanyId,
      status,
      priceModel,
      businessType
    } = values;

    const billingAccount = {
      companyName,
      twilioSecret,
      twilioToken,
      locationId,
      totalToken,
      subscriptionCost: parseFloat(subscriptionCost) || 0,
      reapitCompanyId,
      status,
      priceModel,
      businessType,
      billingDayOfMonth: values.billingDayOfMonth,
      subscriptionDate: subscriptionDate?.toFormat('yyyy-LL-dd'),
      nextSubscriptionDate: nextSubscriptionDate?.toFormat('yyyy-LL-dd'),
      launchDate: launchDate ? launchDate.toFormat('yyyy-LL-dd') : null,
      resetLaunchDate: checkbox,
      // Only meaningful on the update path — the server ignores force on create.
      ...(force ? { force: true } : {})
    };

    const request = selectedBillingAccount
      ? callUpdateBillingAccount(selectedBillingAccount.id, billingAccount)
      : callCreateBillingAccount(billingAccount);
    const successCode = selectedBillingAccount ? 200 : 201;

    setLoading(true);
    request
      .then((res) => {
        if (res.code === successCode) {
          setValues(defaultBillingAccount);
          onSuccess();
        } else {
          // eslint-disable-next-line @typescript-eslint/ban-ts-comment
          //@ts-ignore;
          setError({ title: 'Error', message: res.error?.message ?? '' });
          setTimeout(() => setError(undefined), 5000);
        }
        setLoading(false);
      })
      .catch((err) => {
        setError({
          title: 'Error',
          message: err instanceof Error ? err.message : 'Something went wrong. Please try again.'
        });
        setTimeout(() => setError(undefined), 5000);
        setLoading(false);
      });
  };

  const onSubmit = (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    if (validateFields()) return;
    // Editing a live account with a billing-day change that lands the
    // transitional cycle outside 20-40 days: surface a confirm dialog so
    // the admin explicitly acknowledges the length before we call the
    // server with force=true. Regular changes go through unchanged.
    if (isDayChanged && cycleOutOfBounds) {
      setCycleWarnOpen(true);
      return;
    }
    submitPayload(false);
  };

  const validateFields = () => {
    let error = false;
    setErrors(defaultErrorBillingAccount);
    if (Validator.isEmpty(values.companyName.trim())) {
      error = true;
      setErrors((state) => ({ ...state, companyName: 'Account name is required' }));
    }

    if (!values.locationId && !values.reapitCompanyId) {
      error = true;
      setErrors((state) => ({
        ...state,
        locationId:
          'Either the Reapit Company Id or Location id field is required. Please provide at least one of them or Both.'
      }));
      setErrors((state) => ({
        ...state,
        reapitCompanyId:
          'Either the Reapit Company Id or Location id field is required. Please provide at least one of them or Both.'
      }));
    }

    if (values.locationId && Validator.isEmpty(values.locationId.trim())) {
      error = true;
      setErrors((state) => ({
        ...state,
        locationId: 'Location id should be vaild'
      }));
    }

    if (values.reapitCompanyId && Validator.isEmpty(values.reapitCompanyId.trim())) {
      error = true;
      setErrors((state) => ({
        ...state,
        reapitCompanyId: 'Reapit Company id should be vaild'
      }));
    }

    if (!Validator.isLength(values.companyName.trim(), { min: 3, max: 30 })) {
      error = true;
      setErrors((state) => ({
        ...state,
        companyName: 'Account name must be between 3 and 30 characters in length.'
      }));
    }

    return error;
  };

  return (
    <Modal
      open={open}
      onClose={handleClose}
      sx={{
        display: 'flex',
        alignItems: { xs: 'flex-end', sm: 'center' },
        justifyContent: 'center',
        p: { xs: 0, sm: 2 }
      }}
    >
      <Paper
        sx={{
          width: '100%',
          maxWidth: 780,
          maxHeight: { xs: '95vh', sm: '90vh' },
          display: 'flex',
          flexDirection: 'column',
          borderRadius: { xs: '12px 12px 0 0', sm: 2 },
          outline: 'none'
        }}
      >
        <form onSubmit={onSubmit} style={{ display: 'flex', flexDirection: 'column', minHeight: 0, flex: 1 }}>
          {/* Header */}
          <Box
            sx={{
              px: 3,
              py: 2,
              display: 'flex',
              alignItems: 'center',
              borderBottom: '1px solid',
              borderColor: 'divider'
            }}
          >
            <TextView $fontSize={20} $fontWeight={600}>
              {selectedBillingAccount ? 'Edit Account' : 'Add Account'}
            </TextView>
            <Box sx={{ flex: 1 }} />
            <IconButton onClick={handleClose} aria-label="Close" size="small">
              <Close fontSize="small" />
            </IconButton>
          </Box>

          {/* Body */}
          <Box sx={{ px: 3, py: 2, overflowY: 'auto', flex: 1 }}>
            <Stack spacing={3}>
              {/* Identifiers */}
              <Stack spacing={1.5}>
                <SectionLabel>Identifiers</SectionLabel>
                <TextField
                  label={'Company Name'}
                  name={'companyName'}
                  errorMessage={errors.companyName}
                  fullWidth
                  value={values.companyName}
                  onChange={handleChange('companyName')}
                />
                <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2}>
                  <TextField
                    label={'Location Id'}
                    name={'locationId'}
                    errorMessage={errors.locationId}
                    fullWidth
                    value={values.locationId}
                    onChange={handleChange('locationId')}
                  />
                  <TextField
                    label={'Negotiator Id'}
                    name={'reapitCompanyId'}
                    errorMessage={errors.reapitCompanyId}
                    fullWidth
                    value={values.reapitCompanyId}
                    onChange={handleChange('reapitCompanyId')}
                  />
                </Stack>
              </Stack>

              <Divider />

              {/* Plan & pricing */}
              <Stack spacing={1.5}>
                <SectionLabel>Plan & Pricing</SectionLabel>
                <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2}>
                  <Stack flex={1} width="100%">
                    <TextView $fontSize={14} $fontWeight={400} mb={1}>
                      Business Type
                    </TextView>
                    <Select
                      value={values.businessType}
                      onChange={handleChangeBusinessType}
                      fullWidth
                      size={'small'}
                      sx={{ backgroundColor: '#ffffff', fontSize: 14, height: 40 }}
                      inputProps={{ 'aria-label': 'Business type' }}
                    >
                      {businessTypeOptions.map((opt) => (
                        <MenuItem key={opt.value} value={opt.value}>
                          {opt.label}
                        </MenuItem>
                      ))}
                    </Select>
                  </Stack>
                  <Stack flex={2} width="100%">
                    <TextView $fontSize={14} $fontWeight={400} mb={1}>
                      AI Model
                    </TextView>
                    <Select
                      value={values.priceModel as string}
                      onChange={handleChangePriceModel}
                      fullWidth
                      size={'small'}
                      disabled={priceModelOptionsLoading}
                      sx={{ backgroundColor: '#ffffff', fontSize: 14, height: 40 }}
                      inputProps={{ 'aria-label': 'Price model' }}
                      displayEmpty
                      renderValue={(selected) => {
                        if (priceModelOptionsLoading) {
                          return <span style={{ color: '#94a3b8' }}>Loading plans…</span>;
                        }
                        if (!selected) {
                          return (
                            <span style={{ color: '#94a3b8' }}>
                              {priceModelOptions.length === 0
                                ? 'No plans available — create one in Markup & Margins'
                                : 'Select a plan'}
                            </span>
                          );
                        }
                        const opt = priceModelOptions.find((o) => o.value === selected);
                        if (!opt) return selected as string;
                        return opt.model ? `${opt.label} — ${opt.model}` : opt.label;
                      }}
                    >
                      {priceModelOptions.map((opt) => (
                        <MenuItem key={opt.value} value={opt.value}>
                          <Stack>
                            <TextView $fontSize={14} $fontWeight={500}>
                              {opt.label}
                            </TextView>
                            {opt.model ? (
                              <TextView $fontSize={11} $fontWeight={400} $fontColor="#667">
                                {opt.model}
                              </TextView>
                            ) : null}
                          </Stack>
                        </MenuItem>
                      ))}
                    </Select>
                  </Stack>
                </Stack>
                <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2}>
                  <TextField
                    label={'Total token'}
                    name={'totalToken'}
                    fullWidth
                    errorMessage={errors.totalToken}
                    inputType="number"
                    value={values.totalToken}
                    onChange={handleChange('totalToken')}
                  />
                  <TextField
                    label={'Subscription Cost'}
                    name={'subscriptionCost'}
                    fullWidth
                    inputType="number"
                    value={values.subscriptionCost}
                    onChange={handleChange('subscriptionCost')}
                  />
                </Stack>
              </Stack>

              <Divider />

              {/* Twilio */}
              <Stack spacing={1.5}>
                <SectionLabel>Twilio Credentials</SectionLabel>
                <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2}>
                  <TextField
                    label={'Twilio Secret'}
                    name={'twilioSecret'}
                    errorMessage={errors.twilioSecret}
                    fullWidth
                    value={values.twilioSecret}
                    onChange={handleChange('twilioSecret')}
                  />
                  <TextField
                    label={'Twilio Token'}
                    name={'twilioToken'}
                    errorMessage={errors.twilioToken}
                    fullWidth
                    value={values.twilioToken}
                    onChange={handleChange('twilioToken')}
                  />
                </Stack>
              </Stack>

              <Divider />

              {/* Billing cycle */}
              <Stack spacing={1.5}>
                <SectionLabel>Billing Cycle</SectionLabel>
                <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2} alignItems={{ xs: 'stretch', sm: 'flex-start' }}>
                  <Stack flex={1} width="100%">
                    <Select
                      label="Billing day of month"
                      value={String(values.billingDayOfMonth)}
                      onChange={(e: SelectChangeEvent) => {
                        const day = Number(e.target.value);
                        if (selectedBillingAccount) {
                          // Edit mode: keep the current cycle's start unchanged
                          // and slide the end to the day before the next anchor
                          // of the new billing day. Mirrors the server-side
                          // recompute in updateBillingAccount.
                          const newEnd = nextAnchorAfter(day).minus({ days: 1 });
                          setValues({
                            ...values,
                            billingDayOfMonth: day,
                            nextSubscriptionDate: newEnd
                          });
                        } else {
                          // Create mode: seed both boundaries from today's period.
                          const period = currentPeriodForToday(day);
                          setValues({
                            ...values,
                            billingDayOfMonth: day,
                            subscriptionDate: period.start,
                            nextSubscriptionDate: period.end
                          });
                        }
                      }}
                      fullWidth
                      size={'small'}
                      sx={{ backgroundColor: '#ffffff', fontSize: 14, height: 40 }}
                      inputProps={{ 'aria-label': 'Billing day of month' }}
                      MenuProps={{
                        // Cap the popover to ~8 rows and use dense mode so
                        // 31 items don't stretch the menu the full viewport
                        // height. `dense` beats sx overrides because MUI
                        // resolves touch min-height at the MenuList level.
                        MenuListProps: { dense: true, sx: { py: 0.5 } },
                        PaperProps: {
                          sx: {
                            maxHeight: 260,
                            mt: 0.5,
                            borderRadius: 1.5,
                            boxShadow: '0 8px 24px rgba(15, 23, 42, 0.12)'
                          }
                        }
                      }}
                    >
                      {Array.from({ length: MAX_BILLING_DAY }, (_, i) => i + 1).map((d) => (
                        <MenuItem key={d} value={String(d)} sx={{ fontSize: 13, minHeight: 28, py: 0.25 }}>
                          {d}
                        </MenuItem>
                      ))}
                    </Select>
                  </Stack>
                  <DatePicker
                    sx={{ flex: 1, width: '100%' }}
                    label="Start Billing Cycle"
                    onChange={(subscriptionDate) => setValues({ ...values, subscriptionDate })}
                    value={values.subscriptionDate}
                    disabled={selectedBillingAccount != undefined}
                    minDate={selectedBillingAccount == undefined ? DateTime.now() : null}
                    slotProps={{ textField: { size: 'small' } }}
                  />
                  <DatePicker
                    label="Cycle End (inclusive)"
                    sx={{ flex: 1, width: '100%' }}
                    onChange={(nextSubscriptionDate) => setValues({ ...values, nextSubscriptionDate })}
                    value={values.nextSubscriptionDate}
                    minDate={values.subscriptionDate}
                    slotProps={{ textField: { size: 'small' } }}
                  />
                </Stack>
                <TextView $fontSize={11} $fontWeight={400} $fontColor="#667">
                  Cycle runs from day {values.billingDayOfMonth} of one month to the day before day{' '}
                  {values.billingDayOfMonth} of the next month (inclusive). Days 29–31 are clamped to
                  the last day of shorter months (e.g. day 31 in February = Feb 28/29; in April, June,
                  September, November = the 30th). Every calendar month is billed once — no gaps, no
                  double billing.
                </TextView>
                {isEditMode && isDayChanged && projectedCycleLen !== null && (
                  <Box
                    sx={{
                      p: 1.5,
                      borderRadius: 1,
                      backgroundColor: cycleOutOfBounds ? '#FEF2F2' : '#F0FDF4',
                      border: '1px solid',
                      borderColor: cycleOutOfBounds ? '#FCA5A5' : '#86EFAC'
                    }}
                  >
                    <TextView
                      $fontSize={12}
                      $fontWeight={600}
                      $fontColor={cycleOutOfBounds ? '#B91C1C' : '#166534'}
                    >
                      Transitional cycle: {projectedCycleLen} days
                    </TextView>
                    <TextView $fontSize={11} $fontWeight={400} $fontColor="#475569">
                      Current cycle now runs {values.subscriptionDate?.toFormat('LLL d')} →{' '}
                      {values.nextSubscriptionDate?.toFormat('LLL d, yyyy')}. Next bill fires on{' '}
                      {values.nextSubscriptionDate?.plus({ days: 1 }).toFormat('LLL d, yyyy')}.
                      {cycleOutOfBounds
                        ? ` Outside the normal ${CYCLE_LENGTH_MIN_DAYS}–${CYCLE_LENGTH_MAX_DAYS}-day range — you'll be asked to confirm on save.`
                        : ''}
                    </TextView>
                  </Box>
                )}
                <DatePicker
                  label="Launch date"
                  sx={{ width: '100%', mt: 1 }}
                  onChange={(billing) => setValues({ ...values, launchDate: billing })}
                  value={values.launchDate}
                  minDate={DateTime.now()}
                />
                <FormControlLabel
                  control={
                    <Checkbox
                      defaultChecked={checkbox}
                      checked={checkbox}
                      onChange={(_e, checked) => {
                        if (checked) {
                          setCheckDialog(true);
                        }
                        setCheckBox(false);
                      }}
                    />
                  }
                  label="Reset the SR Token usage data on save"
                />
              </Stack>

              {error && <Alert severity={'error'} title={error.title} message={error.message} />}
            </Stack>
          </Box>

          {/* Footer */}
          <Box
            sx={{
              px: 3,
              py: 2,
              display: 'flex',
              alignItems: 'center',
              gap: 1.5,
              flexWrap: 'wrap',
              borderTop: '1px solid',
              borderColor: 'divider'
            }}
          >
            <Stack direction="row" alignItems="center" spacing={1}>
              <TextView $fontWeight={400} $fontSize={14}>
                Status
              </TextView>
              <Select
                value={values.status}
                onChange={handleChangeStatus}
                size="small"
                sx={{ backgroundColor: '#ffffff', fontSize: 14, minWidth: 120 }}
              >
                <MenuItem value="live">Live</MenuItem>
                <MenuItem value="staging">Staging</MenuItem>
                <MenuItem value="inactive">Inactive</MenuItem>
              </Select>
            </Stack>
            <Box sx={{ flex: 1 }} />
            <Button
              label={'Cancel'}
              variant={'outlined'}
              onClick={handleClose}
              disabled={loading}
            />
            <Button
              label={selectedBillingAccount ? 'Update account' : 'Add account'}
              type="submit"
              loading={loading}
            />
          </Box>
        </form>

        <Dialog
          open={checkDialog}
          onClose={() => {
            setCheckBox(false);
            setCheckDialog(false);
          }}
          aria-labelledby="alert-dialog-title"
          aria-describedby="alert-dialog-description"
        >
          <DialogTitle id="alert-dialog-title">{'Reset SR Token Usage Data?'}</DialogTitle>
          <DialogContent>
            <DialogContentText id="alert-dialog-description">
              This action will clear all current usage data associated with SR Tokens. Ensure that
              you want to proceed with the reset before confirming your selection.
            </DialogContentText>
          </DialogContent>
          <DialogActions>
            <Button
              onClick={() => {
                setCheckBox(false);
                setCheckDialog(false);
              }}
            >
              Disagree
            </Button>
            <Button
              onClick={() => {
                setCheckBox(true);
                setCheckDialog(false);
              }}
            >
              Agree
            </Button>
          </DialogActions>
        </Dialog>

        <Dialog
          open={cycleWarnOpen}
          onClose={() => setCycleWarnOpen(false)}
          aria-labelledby="cycle-warn-title"
          aria-describedby="cycle-warn-description"
        >
          <DialogTitle id="cycle-warn-title">Confirm transitional cycle length</DialogTitle>
          <DialogContent>
            <DialogContentText id="cycle-warn-description">
              Changing the billing day from {originalBillingDay} to {values.billingDayOfMonth} makes
              the current cycle {projectedCycleLen} days long (normal range{' '}
              {CYCLE_LENGTH_MIN_DAYS}–{CYCLE_LENGTH_MAX_DAYS}). Current cycle will run{' '}
              {values.subscriptionDate?.toFormat('LLL d, yyyy')} →{' '}
              {values.nextSubscriptionDate?.toFormat('LLL d, yyyy')} and the next bill will fire on{' '}
              {values.nextSubscriptionDate?.plus({ days: 1 }).toFormat('LLL d, yyyy')}. Proceed?
            </DialogContentText>
          </DialogContent>
          <DialogActions>
            <Button onClick={() => setCycleWarnOpen(false)}>Cancel</Button>
            <Button
              onClick={() => {
                setCycleWarnOpen(false);
                submitPayload(true);
              }}
            >
              Confirm & save
            </Button>
          </DialogActions>
        </Dialog>
      </Paper>
    </Modal>
  );
};
export default AddBillingAccount;
